'use client';

import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { searchDistrictsApi, reverseGeocodeApi } from '@/lib/api';

const LocationPickerMap = dynamic(() => import('@/components/LocationPickerMap'), { ssr: false });

interface LocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  authUser: any;
  userLocation: { lat: number; lng: number; name: string; isLive: boolean } | null;
  isLocating: boolean;
  locationError: string;
  onRequestLiveGps: () => void;
  onSelectHub: (hub: { name: string; lat: number; lng: number; slug?: string; state?: string }) => void;
  onOpenDistrictSelector?: () => void;
  onRequireAuth: () => void;
}

export const POPULAR_HUBS = [
  { name: 'Connaught Place, New Delhi', lat: 28.6315, lng: 77.2167, desc: 'Delhi • Central District & Shivaji Stadium' },
  { name: 'South Delhi / Hauz Khas', lat: 28.5494, lng: 77.2001, desc: 'Delhi • Siri Fort Sports Complex' },
  { name: 'Noida Sector 18, NCR', lat: 28.5708, lng: 77.3271, desc: 'Uttar Pradesh • Noida Stadium & Turf Hub' },
  { name: 'Cyber City, Gurugram', lat: 28.4952, lng: 77.0891, desc: 'Haryana • Gurgaon Sports Hub' },
  { name: 'Bandra West, Mumbai', lat: 19.0596, lng: 72.8295, desc: 'Maharashtra • Bandra Reclamation Sports Complex' },
  { name: 'Andheri West, Mumbai', lat: 19.1363, lng: 72.8277, desc: 'Maharashtra • Sports Complex & Turf Ground' },
  { name: 'Koramangala, Bengaluru', lat: 12.9352, lng: 77.6245, desc: 'Karnataka • Koramangala 4th Block Arena' },
  { name: 'Indiranagar, Bengaluru', lat: 12.9719, lng: 77.6412, desc: 'Karnataka • 100 Feet Road Sports Ground' },
  { name: 'Hitec City, Hyderabad', lat: 17.4474, lng: 78.3762, desc: 'Telangana • Cyberabad Sports Arena' },
  { name: 'Kothrud, Pune', lat: 18.5074, lng: 73.8077, desc: 'Maharashtra • Western Pune Turf Zone' },
  { name: 'Anna Nagar, Chennai', lat: 13.0850, lng: 80.2101, desc: 'Tamil Nadu • Tower Park Sports Grounds' },
  { name: 'Salt Lake Sector V, Kolkata', lat: 22.5804, lng: 88.4378, desc: 'West Bengal • Salt Lake Stadium Zone' },
  { name: 'Vastrapur, Ahmedabad', lat: 23.0360, lng: 72.5298, desc: 'Gujarat • GMDC & Turf Club' },
  { name: 'Sector 17, Chandigarh', lat: 30.7398, lng: 76.7827, desc: 'Chandigarh • Sector 17 Stadium Arena' },
  { name: 'C-Scheme, Jaipur', lat: 26.9124, lng: 75.7873, desc: 'Rajasthan • SMS Stadium Zone' },
  { name: 'Gomti Nagar, Lucknow', lat: 26.8530, lng: 81.0003, desc: 'Uttar Pradesh • Ekana Sports Hub' },
];

export default function LocationModal({
  isOpen,
  onClose,
  authUser,
  userLocation,
  isLocating,
  locationError,
  onRequestLiveGps,
  onSelectHub,
  onOpenDistrictSelector,
}: LocationModalProps) {
  const [activeMode, setActiveMode] = useState<'gps' | 'map' | 'search'>('gps');

  // Map Picker State
  const [mapLat, setMapLat] = useState(userLocation?.lat || 28.6139);
  const [mapLng, setMapLng] = useState(userLocation?.lng || 77.2090);
  const [mapAddress, setMapAddress] = useState(userLocation?.name || 'Selected Map Location');
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);

  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (userLocation) {
      setMapLat(userLocation.lat);
      setMapLng(userLocation.lng);
      setMapAddress(userLocation.name);
    }
  }, [userLocation]);

  // Reverse geocode when map pin coordinates change
  const handleMapPinChange = async (lat: number, lng: number) => {
    setMapLat(lat);
    setMapLng(lng);
    setIsResolvingAddress(true);
    try {
      const info = await reverseGeocodeApi(lat, lng);
      if (info?.displayName) {
        setMapAddress(info.displayName);
      } else {
        setMapAddress('Selected Map Location');
      }
    } catch {
      setMapAddress('Selected Map Location');
    } finally {
      setIsResolvingAddress(false);
    }
  };

  // Search districts API
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const districts = await searchDistrictsApi(searchQuery.trim(), 15);
        if (districts && districts.length > 0) {
          setSearchResults(districts);
        } else {
          const filtered = POPULAR_HUBS.filter(h =>
            h.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            h.desc.toLowerCase().includes(searchQuery.toLowerCase())
          );
          setSearchResults(filtered);
        }
      } catch {
        const filtered = POPULAR_HUBS.filter(h =>
          h.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setSearchResults(filtered);
      } finally {
        setIsSearching(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.82)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '520px',
          background: '#0B0D12',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.95)',
          position: 'relative',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '19px', fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.01em' }}>
              Set Your Sports Location
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
              Pan-India sports network • 779 Districts & Venues
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255, 255, 255, 0.7)',
              cursor: 'pointer',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* 3 First-Class Selection Options */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '6px',
          background: 'rgba(255, 255, 255, 0.04)',
          padding: '4px',
          borderRadius: '10px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          marginBottom: '20px',
        }}>
          <button
            type="button"
            onClick={() => setActiveMode('gps')}
            style={{
              padding: '8px 6px',
              borderRadius: '8px',
              border: 'none',
              background: activeMode === 'gps' ? '#FFFFFF' : 'transparent',
              color: activeMode === 'gps' ? '#000000' : 'rgba(255, 255, 255, 0.7)',
              fontSize: '12px',
              fontWeight: activeMode === 'gps' ? 700 : 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s ease',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10" />
              <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
            </svg>
            <span>Live GPS</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveMode('map')}
            style={{
              padding: '8px 6px',
              borderRadius: '8px',
              border: 'none',
              background: activeMode === 'map' ? '#FFFFFF' : 'transparent',
              color: activeMode === 'map' ? '#000000' : 'rgba(255, 255, 255, 0.7)',
              fontSize: '12px',
              fontWeight: activeMode === 'map' ? 700 : 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s ease',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span>Select on Map</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveMode('search')}
            style={{
              padding: '8px 6px',
              borderRadius: '8px',
              border: 'none',
              background: activeMode === 'search' ? '#FFFFFF' : 'transparent',
              color: activeMode === 'search' ? '#000000' : 'rgba(255, 255, 255, 0.7)',
              fontSize: '12px',
              fontWeight: activeMode === 'search' ? 700 : 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s ease',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <span>Search District</span>
          </button>
        </div>

        {/* MODE 1: USE MY CURRENT LOCATION (GPS) */}
        {activeMode === 'gps' && (
          <div style={{ marginBottom: '18px' }}>
            <div
              style={{
                background: userLocation?.isLive ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.03)',
                border: userLocation?.isLive ? '1px solid rgba(255, 255, 255, 0.25)' : '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '12px',
                padding: '18px',
                marginBottom: '16px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: userLocation?.isLive ? '#3B82F6' : 'rgba(255, 255, 255, 0.3)',
                      boxShadow: userLocation?.isLive ? '0 0 10px #3B82F6' : 'none',
                      display: 'inline-block',
                    }}
                  />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#FFFFFF' }}>
                      {userLocation?.isLive ? userLocation.name : 'GPS Location Not Locked'}
                    </div>
                    <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '2px' }}>
                      {userLocation?.isLive ? 'Live GPS Active • Privacy Shield Enabled' : 'Click below to acquire device GPS and connect to nearest district'}
                    </div>
                  </div>
                </div>
                {userLocation?.isLive && (
                  <span
                    style={{
                      fontSize: '10px',
                      color: '#000000',
                      background: '#FFFFFF',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontWeight: 700,
                      letterSpacing: '0.5px',
                    }}
                  >
                    LOCKED
                  </span>
                )}
              </div>

              {locationError && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#FF6B6B',
                    background: 'rgba(255, 107, 107, 0.1)',
                    border: '1px solid rgba(255, 107, 107, 0.25)',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    marginBottom: '14px',
                  }}
                >
                  {locationError}
                </div>
              )}

              <button
                type="button"
                onClick={onRequestLiveGps}
                disabled={isLocating}
                style={{
                  width: '100%',
                  height: '42px',
                  fontSize: '13px',
                  fontWeight: 700,
                  opacity: isLocating ? 0.7 : 1,
                  cursor: isLocating ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  background: '#FFFFFF',
                  color: '#000000',
                  border: 'none',
                  borderRadius: '8px',
                }}
              >
                {isLocating ? (
                  <>
                    <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="16" />
                    </svg>
                    <span>Detecting your current location...</span>
                  </>
                ) : (
                  <>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="10" />
                      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
                    </svg>
                    <span>Detect my current location</span>
                  </>
                )}
              </button>

              <div style={{ marginTop: '14px', fontSize: '11px', color: 'rgba(255, 255, 255, 0.55)', lineHeight: 1.5, background: 'rgba(255, 255, 255, 0.03)', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.06)', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--primary, #8B5CF6)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                <span><strong>Seeing a different city?</strong> Wi-Fi and desktop browsers sometimes resolve location through your telecom ISP routing station. If your detected city is incorrect, switch to <strong>Select on Map</strong> or <strong>Search District</strong> above to set your exact location.</span>
              </div>
            </div>
          </div>
        )}

        {/* MODE 2: SELECT ON MAP */}
        {activeMode === 'map' && (
          <div style={{ marginBottom: '18px' }}>
            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', marginBottom: '6px' }}>
                Interactive Turf Pin-Dropper
              </div>
              <div style={{ height: '230px', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                <LocationPickerMap
                  lat={mapLat}
                  lng={mapLng}
                  onChange={handleMapPinChange}
                />
              </div>
            </div>

            <div style={{
              background: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '10px',
              padding: '12px',
              marginBottom: '14px',
            }}>
              <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)' }}>Pin Location:</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#FFFFFF', marginTop: '2px' }}>
                {isResolvingAddress ? 'Resolving address and district...' : mapAddress}
              </div>
              <div style={{ fontSize: '11px', color: '#60A5FA', marginTop: '2px' }}>
                Pin Position Locked (Coarse Search Radius)
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                onSelectHub({
                  name: mapAddress,
                  lat: mapLat,
                  lng: mapLng,
                });
                onClose();
              }}
              style={{
                width: '100%',
                height: '40px',
                background: '#FFFFFF',
                color: '#000000',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Confirm Selected Map Location
            </button>
          </div>
        )}

        {/* MODE 3: SEARCH ON MAP / SEARCH DISTRICT */}
        {activeMode === 'search' && (
          <div style={{ marginBottom: '18px' }}>
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <input
                type="text"
                placeholder="Search district, city, state, or ground..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  height: '42px',
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '10px',
                  padding: '0 14px 0 38px',
                  color: '#FFFFFF',
                  fontSize: '13px',
                  outline: 'none',
                }}
              />
              <div style={{ position: 'absolute', left: '12px', top: '12px', color: 'rgba(255, 255, 255, 0.4)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              {isSearching && (
                <div style={{ position: 'absolute', right: '12px', top: '13px' }}>
                  <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="16" />
                  </svg>
                </div>
              )}
            </div>

            {searchResults.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '200px', overflowY: 'auto' }}>
                {searchResults.map((item, idx) => (
                  <button
                    key={item.id || item.slug || idx}
                    type="button"
                    onClick={() => {
                      onSelectHub({
                        name: item.name ? `${item.name}${item.state ? ', ' + item.state : ''}` : item.name,
                        lat: item.lat,
                        lng: item.lng,
                        slug: item.slug,
                        state: item.state,
                      });
                      onClose();
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      background: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#FFFFFF' }}>
                        {item.name}
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)' }}>
                        {item.state || item.desc || 'District Hub'}
                      </div>
                    </div>
                    <span style={{ fontSize: '11px', color: '#60A5FA', fontWeight: 600 }}>Select</span>
                  </button>
                ))}
              </div>
            ) : searchQuery.trim().length >= 2 && !isSearching ? (
              <div style={{ textAlign: 'center', padding: '16px', color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>
                No exact district matched. Try searching another city or use map pin-dropper.
              </div>
            ) : null}
          </div>
        )}

        {/* Indian Metros Quick Selector */}
        <div>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255, 255, 255, 0.4)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
            Popular Indian Metros
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', maxHeight: '180px', overflowY: 'auto' }}>
            {POPULAR_HUBS.map((hub) => {
              const isSelected = !userLocation?.isLive && userLocation?.name === hub.name;
              return (
                <button
                  key={hub.name}
                  type="button"
                  onClick={() => {
                    onSelectHub(hub);
                    onClose();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 10px',
                    borderRadius: '8px',
                    background: isSelected ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.03)',
                    border: isSelected ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.06)',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#FFFFFF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {hub.name.split(',')[0]}
                    </div>
                    <div style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {hub.name.split(',')[1] || hub.desc}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {onOpenDistrictSelector && (
          <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: '14px' }}>
            <button
              type="button"
              onClick={() => {
                onClose();
                onOpenDistrictSelector();
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: '#FFFFFF',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: '12px', fontWeight: 600 }}>Explore All 779 Indian Districts Directory</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
